import { Box, Card, Divider, Flex, Group, ScrollArea, Skeleton, Stack } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { PriceOfferListResponse } from 'api/actions/price-offer-list/price-offer-list-response';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import NumberBadge from 'components/NumberBadge';
import PriceOfferActions from 'components/tables/data/price-offer/PriceOfferActions';
import PriceOfferStatusIcon from 'components/tables/data/price-offer/PriceOfferStatusIcon';
import H3Medium from 'components/typography/H3Medium';
import P2Medium from 'components/typography/P2Medium';
import P3Regular from 'components/typography/P3Regular';
import panic from 'errors/panic';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PRICE_OFFERS_PAGE_PATH } from 'routes/paths';

/**
 * Parameters of the ActionablePriceOffersCard component.
 */
export interface ActionablePriceOffersCardProps {
  limit?: number;
  onDataChange?: () => void;
}

/**
 * Displays price offers where the current user can provide or close the offer.
 */
export default function ActionablePriceOffersCard({ limit = 10, onDataChange }: ActionablePriceOffersCardProps = {}) {
  const { getAction } = useApi();
  const { isMobile } = useResponsiveDesign();
  const [priceOffers, setPriceOffers] = useState<PriceOfferListResponse>([]);
  const [loading, setLoading] = useState(true);
  const somePriceOffers = useMemo(() => priceOffers.slice(0, limit), [priceOffers, limit]);

  /**
   * Loads actionable price offers for the current user.
   */
  const loadPriceOffers = useCallback(() => {
    setLoading(true);

    const action = getAction('PriceOfferList');

    action({ query: { filters: { 'actionable.eq': true } } })
      .then((priceOffers) => setPriceOffers(Array.isArray(priceOffers) ? priceOffers : []))
      .catch(panic)
      .finally(() => setLoading(false));
  }, [getAction]);

  /**
   * Refreshes the card after a price offer action changes data.
   */
  const handlePriceOfferChanged = useCallback(() => {
    loadPriceOffers();
    onDataChange?.();
  }, [loadPriceOffers, onDataChange]);

  useEffect(() => {
    loadPriceOffers();
  }, [loadPriceOffers]);

  if (loading) {
    return <Skeleton radius={4} w="100%" mih={400} />;
  }

  return (
    <Card p={24} h="fit-content">
      <Group spacing={4} pb={24}>
        <H3Medium color="gray.8">Cenové ponuky čakajúce na akciu</H3Medium>
        <Box sx={{ transform: 'scale(0.66)' }}>
          <NumberBadge variant="gray" value={priceOffers.length} />
        </Box>
      </Group>
      <ScrollArea h={416} mr={-20} pr={20}>
        {somePriceOffers.map((priceOffer) => (
          <Stack key={priceOffer.priceOfferId} spacing={0}>
            <Divider color="gray.1" />
            <Group noWrap mih={82} spacing={16} pt={12} pb={16} position="apart">
              <Stack spacing={4}>
                <Group spacing={12}>
                  <P2Medium color="gray.8">{priceOffer.device.deviceName}</P2Medium>
                  <PriceOfferStatusIcon status={priceOffer.status} />
                </Group>
                <P3Regular color="gray.7">
                  #{priceOffer.priceOfferId} • {priceOffer.assignedTo.fullName} •{' '}
                  {priceOffer.organization.organizationName} ({priceOffer.department.departmentName})
                </P3Regular>
              </Stack>
              <PriceOfferActions
                priceOffer={priceOffer}
                compactOnTablet={isMobile}
                keepPrimaryActionOnMobile
                onChanged={handlePriceOfferChanged}
              />
            </Group>
          </Stack>
        ))}
      </ScrollArea>
      <Divider color="gray.1" />
      <Box pt={16} sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
        <Flex align="center" justify="space-between" gap={16} miw="100%" w="max-content">
          <Button
            component={Link}
            to={PRICE_OFFERS_PAGE_PATH.original}
            variant="subtle"
            size="sm"
            rightIcon={<IconChevronRight size={16} />}
          >
            Všetky cenové ponuky
          </Button>
          <P3Regular color="gray.7">
            Zobrazené: {somePriceOffers.length} / {priceOffers.length}
          </P3Regular>
        </Flex>
      </Box>
    </Card>
  );
}
